import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom'; 

import { useNotification } from '../../utils/notifications';

import { 
    getToken,
    getUuid,
    urlHome,
    urlReports,
    useMobileView,
    getUserFullName,
    shortname,
    getOrganization,
    getOrganizations,
    getActiveOrganization,
    languages,
    Language,
    enUS,
    LanguageShort,
    dashboardLabel,
    selectLanguageLabel,
    languagesList,
    setLanguage,
    signOutLabel,
    getMenuLabels,
    organizationsLabel,
    usersLabel,
    reportsLabel,
    settingsLabel,
    selectOrganizationLabel,
    urlOrganizations,
    urlUsers,
    urlSettings,
    setActiveOrganization
} from '../../utils/constants';

import MenuItem from '../../parts/MenuItem/MenuItem';

import style from './style.module.css'

import Icons from '../../parts/Icons/Icons';

import { faHouse, faRightFromBracket, faGlobe, faArrowUpRightFromSquare, faXmark, faUsers, faBuilding, faChartArea, faGear } from '@fortawesome/free-solid-svg-icons';

import { changeLanguage } from '../../services/authApi';

import { sysLabels } from '../../data/translations';

interface HeaderProps {
  isAuthenticated: boolean;
  language: string;
  handleSignOut: () => void;
}

type MenuItemLabel = {
  EN: string;
  RU: string;
  NL: string;
};

type MenuItemLabels = {
  [key: string]: MenuItemLabel;
};

const Header: React.FC<HeaderProps> = ({isAuthenticated, handleSignOut, language}) => {

  const activeToken = getToken();
  const uuid = getUuid();
  const navigate = useNavigate();
  const notify = useNotification()

  const isMobile = useMobileView();

  const dateYear = process.env.REACT_APP_YEAR;
  const disclaimer = process.env.REACT_APP_DISCLAIMER;
  const version = process.env.REACT_APP_VERSION;

  const [organizationName,setOrganizationName] = useState(getOrganization()['name'])
  const [organizationShortName,setOrganizationShortName] = useState(getOrganization()['shortname'])

  const [activeOrganizationName,setActiveOrganizationName] = useState(getActiveOrganization()?.name ?? "Unknown")
  const [activeOrganizationOwner,setActiveOrganizationOwner] = useState(getActiveOrganization()?.owner ?? false)

  const [menuItemLabels, setMenuItemLabels] = useState<MenuItemLabels>({});

  useEffect(() => {
    if (Object.keys(menuItemLabels).length === 0) {
      const labels = getMenuLabels();
      try {
        const parsedLabels = JSON.parse(labels);
        setMenuItemLabels(parsedLabels);
      } catch (e) {
        console.error('Failed to parse menu labels', e);
      }
    }
  }, [menuItemLabels]);

  const [userMenuOpened,setUserMenuOpened] = useState<boolean>(false);
  const [languageMenuOpened,setLanguageMenuOpened] = useState<boolean>(false);
  const [organizationListOpened,setOrganizationListOpened] = useState<boolean>(false);

  const activeLanguage = (() => {
    if (!language) return languages(enUS);
    try {
      return JSON.parse(language);
    } catch {
      return null;
    }
  })();

  const getLocalLabel = (key: keyof typeof menuItemLabels, lang: LanguageShort) => {
    return menuItemLabels?.[key]?.[lang] || '';
  };

  const handleMenu = () => {
    if(userMenuOpened){
      setUserMenuOpened(false)
      return
    }
    setUserMenuOpened(true)
  }

  const handleOrganizationSwitch = () => {
    if(organizationListOpened){
      setOrganizationListOpened(false)
      return
    }
    setOrganizationListOpened(true)
  }

  const handleOrganizationSelect = (org_id: number) => {
      const organizationsList = getOrganizations(); 
      const selectedOrg = organizationsList.find(org => org.id === org_id);
      if (!selectedOrg) {
          notify('Error', 3000, '#FF0000')
          return;
      }
      notify(getLocalLabel(organizationsLabel, activeLanguage.short) + ': ' + selectedOrg.name, 3000)
      setActiveOrganizationName(selectedOrg.name)
      setActiveOrganizationOwner(selectedOrg.owner)
      setActiveOrganization(org_id)
      handleOrganizationSwitch();
  };

  const handleLanguageSelect = () => {
    setLanguageMenuOpened(true)
  }

  const handleLanguage = async (lang: string | any) => {
    const newLanguage = languages(lang)

    await changeLanguage({ 
      uuid: uuid, 
      token: activeToken, 
      language: newLanguage.short
    });

    setLanguage(JSON.stringify(newLanguage))
    setLanguageMenuOpened(false)
  }


  const handleGoHome = () => {
    navigate(urlHome);
  }

  const renderOrganization = () => (
      <div className={style.modal}>
        <div className={style.wrap}>
          <div className={`${style.langBox} ${style.orgBox}`}>
              <Icons icon={faBuilding} />
              <div className={style.langTitle}>{getLocalLabel(selectOrganizationLabel, activeLanguage.short)}</div>
              {
                getOrganizations().map((org) => (
                  <div className={style.org} key={org.id} onClick={() => handleOrganizationSelect(org.id)}>
                    {org.name} {org.owner ? <span>Owner</span> : ''}
                  </div>
                ))
              }
          </div>
        </div>
      </div>
    )

  const renderLanguage = () => (
    <div className={style.modal}>
      <div className={style.wrap}>
        <div className={style.langBox}>
            <Icons icon={faGlobe} />
            <div className={style.langTitle}>{getLocalLabel(selectLanguageLabel, activeLanguage.short)}</div>
            {
              Object.entries(languagesList).map(([key, lang]) => (
                <div className={style.lang} key={key} onClick={() => handleLanguage(key)}>
                  {lang.flag} {lang.name}
                </div>
              ))
            }
        </div>
      </div>
    </div>
  )

    const renderAppMenu = () => (
      <div className={`${style.userMenuWrapper}`}>
        <div className={style.appMenu}>

          <div className={style.userMenuIcon}>
            <div className={style.userMenuIconLeft}>
              <div className={style.labelImg} onClick={handleGoHome}></div>
              <div className={style.adminText}>Admin</div>
            </div> 
          </div>
          <div className={style.sideMenu}>
              <MenuItem icon={faHouse} label={getLocalLabel(dashboardLabel, activeLanguage.short)} link={urlHome} />
              <MenuItem icon={faBuilding} label={getLocalLabel(organizationsLabel, activeLanguage.short)} link={urlOrganizations} />
              <MenuItem icon={faUsers} label={getLocalLabel(usersLabel, activeLanguage.short)} link={urlUsers} />
              <MenuItem icon={faChartArea} label={getLocalLabel(reportsLabel, activeLanguage.short)} link={urlReports} />
              <MenuItem icon={faGear} label={getLocalLabel(settingsLabel, activeLanguage.short)} link={urlSettings} />
          </div>
          <div className={style.disclaimer}>
              <MenuItem icon={faGlobe} label={activeLanguage.name} flag={activeLanguage.flag} link={'#'} click={handleLanguageSelect} />
              <div className={style.disclaimer_text}>
                  &copy; {dateYear} {disclaimer} &nbsp;&nbsp;&bull;&nbsp;&nbsp; {version}
              </div>
          </div>

        </div>
    </div>
  )

  const renderUserMenuIcon = () => (
    <div className={style.userIcon}>{shortname(getUserFullName())}</div>
  )

  const renderUserMenu = () => (
    <div className={`${style.userMenuWrapper} ${userMenuOpened ? style.active : ''}`}>
      <div className={style.userMenu}>

        <div className={style.userMenuIcon}>
          <div className={style.userMenuIconLeft}>
              <div className={style.userMilLeft}>
                  {renderUserMenuIcon()}
              </div><div className={style.userMilRight}>
                <div className={style.userName}>{getUserFullName()}</div> 
                <div className={style.userCompany}>{organizationName}</div>
              </div>
          </div><div className={style.userMenuIconRight}>
                <div onClick={handleMenu} className={style.userMirClose}>
                    <Icons icon={faXmark} />
                </div>
          </div>  
        </div>

        <div className={`${style.sideMenu} ${style.light} ${style.short}`}>
          <MenuItem icon={faRightFromBracket} label={sysLabels[signOutLabel][((activeLanguage as unknown) as Language).short || 'EN']} link={'/'} click={handleSignOut} mode={'light'} />
        </div>
      </div>
    </div>
  );
  
  return (
    <>
    <div className={style.wrapper}>
      <div className={style.labelContainer}></div>
      <div className={style.loginWrapper}>

        <div className={style.organizationSwitch} onClick={handleOrganizationSwitch}>
          <div className={style.orgName}>
            <div className={style.orgNameWrap}>{activeOrganizationName}</div>{activeOrganizationOwner ? <span>Owner</span> : ''}</div>

        </div><div className={style.headerUserMenu} onClick={handleMenu}>
          <div className={style.userCompany}>
            <div className={style.ucName}>
              {organizationShortName}
            </div>
          </div><div className={style.userUser}>
              {renderUserMenuIcon()}
          </div>
        </div>
      </div>
      </div>
      {isAuthenticated && renderAppMenu()}
      {isAuthenticated && userMenuOpened && renderUserMenu()}
      {languageMenuOpened && renderLanguage()}
      {organizationListOpened && renderOrganization()}
    </>
  );
};

export default Header;